import { Sparkles } from "lucide-react";

export function SectionHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
      <div>
        {eyebrow && <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">{eyebrow}</div>}
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tighter">
          {title}
        </h2>
        {subtitle && <p className="text-sm text-zinc-400 mt-1 max-w-xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function GlassCard({ children, className = "", testid }) {
  return (
    <div
      className={`glass rounded-3xl p-6 md:p-7 relative overflow-hidden ${className}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export function ScoreGauge({ value = 0, label = "Score", size = 180, tone = "gradient" }) {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = (size - 24) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (clamped / 100) * circ;
  const strokeColor = tone === "gradient" ? "url(#nexusGrad)" : tone === "warn" ? "#FFB020" : tone === "danger" ? "#FF3B6B" : "#66D19E";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="nexusGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FF3B6B" />
            <stop offset="50%" stopColor="#9D4EDD" />
            <stop offset="100%" stopColor="#2EC5FF" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="rgba(255,255,255,0.06)" strokeWidth="10" fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={strokeColor}
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(.16,1,.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black text-4xl tracking-tighter nexus-gradient-text tabular-nums">{clamped}</div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export function StatPill({ label, value, hint, tone = "default" }) {
  const t = tone === "up" ? "text-emerald-300" : tone === "down" ? "text-nexus-pink" : "text-white";
  return (
    <div className="p-4 rounded-2xl bg-white/[0.02] hairline">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className={`font-display font-bold text-2xl tabular-nums ${t}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export function BadgePill({ label, icon: Icon, tone = "default" }) {
  const map = {
    default: "border-white/15 bg-white/5 text-zinc-200",
    ready: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    warn: "border-yellow-500/30 bg-yellow-500/10 text-yellow-300",
    growth: "border-nexus-blue/30 bg-nexus-blue/10 text-nexus-blue",
    hot: "border-nexus-pink/30 bg-nexus-pink/10 text-nexus-pink",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${map[tone] || map.default}`}>
      {Icon && <Icon className="h-3 w-3" />} {label}
    </span>
  );
}

export function InsightBubble({ children, testid }) {
  return (
    <div className="p-4 rounded-2xl bg-gradient-to-br from-nexus-purple/10 via-nexus-pink/5 to-nexus-blue/10 hairline flex gap-3" data-testid={testid}>
      <Sparkles className="h-4 w-4 text-nexus-pink shrink-0 mt-0.5" />
      <p className="text-sm text-zinc-100 leading-relaxed">{children}</p>
    </div>
  );
}

export function formatNum(n) {
  if (n === undefined || n === null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
